/**
 * Parse pipeline: raw text -> header/rows -> repairs -> schema inference.
 * Owns the freemium 500-row gate, NDJSON support, and repair hint reporting.
 */
import { detectDelimiter, parseCsv } from './csv';
import { flattenRecord, parseJson } from './json';
import { inferColumns } from './infer';
import { applyRepairs, countRepairHints } from './repair';
import { ALL_REPAIRS, NO_REPAIRS, REPAIR_ORDER, type InputFormat, type ParseRequest, type ParseResult, type RepairConfig } from './types';

export const FREE_ROW_LIMIT = 500;

const REPAIR_LABELS: Record<string, string> = {
  trimWhitespace: 'Trim whitespace',
  standardizeNull: 'Standardize nulls',
  stripCurrency: 'Strip currency',
  normalizeDates: 'Normalize dates → ISO-8601',
  normalizeHeaders: 'Normalize headers',
};

function extOf(fileName: string): string {
  const m = /\.([a-z0-9]+)$/i.exec(fileName.trim());
  return m ? m[1].toLowerCase() : '';
}

export function sniffFormat(text: string, fileName: string): 'csv' | 'json' | 'ndjson' {
  const ext = extOf(fileName);
  if (ext === 'ndjson' || ext === 'jsonl') return 'ndjson';
  if (ext === 'json') return 'json';
  if (ext === 'csv' || ext === 'tsv' || ext === 'txt') return 'csv';
  const t = text.replace(/^\uFEFF/, '').trimStart();
  if (t.startsWith('[') || (t.startsWith('{') && t.includes('\n') === false)) {
    // Single object or array -> json; multi-line objects -> ndjson handled below.
  }
  if (t.startsWith('[') || t.startsWith('{')) {
    // Distinguish NDJSON (multiple JSON objects, one per line).
    const lines = t.split(/\r?\n/).filter((l) => l.trim() !== '');
    if (lines.length > 1) {
      let allJson = true;
      for (const line of lines) {
        try {
          JSON.parse(line);
        } catch {
          allJson = false;
          break;
        }
      }
      if (allJson) return 'ndjson';
    }
    return 'json';
  }
  return 'csv';
}

function parseNdjson(text: string): { header: string[]; rows: string[][] } {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length === 0) return { header: [], rows: [] };
  const records: Record<string, unknown>[] = lines.map((line, i) => {
    try {
      const v: unknown = JSON.parse(line);
      if (typeof v === 'object' && v !== null && !Array.isArray(v)) return v as Record<string, unknown>;
      return { value: v };
    } catch {
      throw new Error(`Invalid NDJSON on line ${i + 1}: each line must be a JSON object.`);
    }
  });
  const header: string[] = [];
  const seen = new Set<string>();
  for (const rec of records) {
    for (const k of Object.keys(flattenRecord(rec))) {
      if (!seen.has(k)) {
        seen.add(k);
        header.push(k);
      }
    }
  }
  return { header, rows: records.map((rec) => {
    const flat = flattenRecord(rec);
    return header.map((k) => (k in flat ? flat[k] : ''));
  }) };
}

export function runParse(request: ParseRequest): ParseResult {
  const t0 = performance.now();
  const { text, fileName } = request;
  const repairs: RepairConfig = request.repairs ?? NO_REPAIRS;
  const isPro = request.isPro ?? false;

  if (text.trim() === '') throw new Error('Empty input: paste CSV/JSON or drop a file.');

  const requested = request.format ?? 'auto';
  const format = requested === 'auto' ? sniffFormat(text, fileName) : requested;

  let headers: string[];
  let rawRows: (string | null)[][];
  try {
    if (format === 'json') {
      // Explicit .json that is actually NDJSON still parses (be liberal).
      try {
        const parsed = parseJson(text);
        headers = parsed.header;
        rawRows = parsed.rows;
      } catch {
        const nd = parseNdjson(text);
        headers = nd.header;
        rawRows = nd.rows;
      }
      if (headers.length === 0) throw new Error('No columns found — JSON input must contain objects.');
    } else if (format === 'ndjson') {
      const nd = parseNdjson(text);
      headers = nd.header;
      rawRows = nd.rows;
    } else {
      const delimiter = request.delimiter ?? detectDelimiter(text);
      const table = parseCsv(text, delimiter);
      if (table.length === 0) throw new Error('No header row found.');
      headers = table[0];
      rawRows = table.slice(1);
    }
  } catch (err) {
    if (format === 'json' || extOf(fileName) === 'json') {
      const msg = err instanceof Error ? err.message : String(err);
      if (/JSON|NDJSON/i.test(msg)) throw err;
      throw new Error(`Invalid JSON in ${fileName || 'input'}: ${msg}`);
    }
    throw err;
  }

  if (headers.length === 0) throw new Error('No columns found.');
  if (rawRows.length === 0) throw new Error('No data rows found.');

  // Normalize ragged rows before inference.
  const width = headers.length;
  const normalized: (string | null)[][] = rawRows.map((r) => {
    const next = r.slice(0, width);
    while (next.length < width) next.push(null);
    return next.map((v) => (v === undefined ? null : v));
  });

  const rawRowCount = normalized.length;

  // Freemium gate: free tier analyzes the first 500 rows.
  let truncated = false;
  let working = normalized;
  if (!isPro && normalized.length > FREE_ROW_LIMIT) {
    working = normalized.slice(0, FREE_ROW_LIMIT);
    truncated = true;
  }

  // Repair hints are measured on the working set even for disabled repairs.
  const preColumns = inferColumns(headers, working);
  const hints = countRepairHints(headers, working, preColumns);
  const outcome = applyRepairs(headers, working, preColumns, repairs);
  const columns = inferColumns(outcome.headers, outcome.rows);

  const repairEntries = REPAIR_ORDER.map((key) => ({
    key,
    applied: repairs[key],
    changed: outcome.counts[key] ?? hints[key] ?? 0,
    label: REPAIR_LABELS[key] ?? key,
  }));
  // Report hints for disabled repairs so the UI can show "would touch N cells".
  for (const entry of repairEntries) {
    if (!entry.applied) entry.changed = hints[entry.key] ?? 0;
  }

  const warnings: string[] = [];
  if (truncated) {
    warnings.push(
      `Free plan: analyzing the first ${FREE_ROW_LIMIT} of ${rawRowCount} rows. Activate Pro for unlimited rows.`,
    );
  }

  return {
    fileName,
    format,
    headers: outcome.headers,
    columns,
    rows: outcome.rows,
    rowCount: outcome.rows.length,
    rawRowCount,
    truncated,
    warnings,
    repairs: repairEntries,
    parseMs: performance.now() - t0,
  };
}

export { ALL_REPAIRS, NO_REPAIRS };
export type { InputFormat, ParseRequest };
